import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs';
import { StyleClassModule } from 'primeng/styleclass';
import { AppConfigurator } from './app.configurator';
import { LayoutService } from '../service/layout.service';
import { AvatarModule } from 'primeng/avatar';
import { Popover } from 'primeng/popover';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { MenuService } from '../service/menu.service';
import { Message } from 'primeng/message';
import { AuthService } from '@/core/auth/auth.service';
import { UserData } from '@/core/auth/auth.model';
import { DashboardPerfilService } from '@/features/portal/dashboard-profile/dashboard-profile.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { LoggerService } from '@/core/services/logger.service';

/** Barra superior: identidade do sistema, foto/menu do usuário e central de notificações. */
@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, StyleClassModule, AppConfigurator, AvatarModule, AvatarGroupModule, OverlayBadgeModule, Popover, InputGroupAddonModule, ButtonModule, InputTextModule, Message, AppConfigurator],
    templateUrl: 'app.topbar.html',
    styles: `
        .notification-list > * {
            padding: 5px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppTopbar implements OnInit {
    // Dados de usuário de exemplo. Em uma aplicação real, estes viriam de um serviço de autenticação.
    readonly user = signal<UserData | undefined>(undefined);
    photoUrl = signal<string | undefined>(undefined);
    readonly sistema = signal('SICOM');

    isRootRoute = signal(false);
    protected layoutConfigVisible = false;
    readonly notificacoes = computed(() => this.notificacoesService.notificacoes());
    readonly totalNotificacoes = computed(() => this.notificacoes().length);
    private destroyRef = inject(DestroyRef);

    readonly notificacoesPopover = viewChild<Popover>('notficacoes');
    readonly opPopover = viewChild<Popover>('op');

    readonly layoutService = inject(LayoutService);
    private readonly authService = inject(AuthService);
    private readonly menuService = inject(MenuService);
    private readonly router = inject(Router);
    private readonly perfilService = inject(DashboardPerfilService);
    private readonly notificacoesService = inject(NotificacoesService);
    private readonly logger = inject(LoggerService);

    constructor() {
        this.loadUserInfo();
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((event: NavigationEnd) => {
                this.isRootRoute.set(event.urlAfterRedirects.split('?')[0] === '/' || event.urlAfterRedirects.split('?')[0] === '' || event.urlAfterRedirects.split('?')[0] === '/dashboard');
            });
    }

    private async loadUserInfo() {
        this.user.set(await this.authService.getUserInfo());
        if (this.user()?.photoUrl) {
            this.photoUrl.set(this.user()?.photoUrl);
        }

        if (this.user()) {
            this.loadUserProfile();
        }

        this.observePerfilChanges();
    }

    private observePerfilChanges() {
        this.perfilService.perfilAtualizado$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this.user()) {
                this.loadUserProfile();
            }
        });
    }

    private loadUserProfile() {
        this.perfilService.buscarPerfil().subscribe({
            next: (response) => {
                const res = Array.isArray(response) ? response[0] : response;
                if (res?.foto) {
                    const foto = res.foto.startsWith('data:image') ? res.foto : `data:image/jpeg;base64,${res.foto}`;
                    this.photoUrl.set(foto);
                    if (this.user()) {
                        this.user.update((u) => (u ? { ...u, photoUrl: foto } : u));
                    }
                } else {
                    this.photoUrl.set('images/notpicture.svg');
                }
            },
            error: (err) => {
                this.logger.error('Erro ao carregar foto do perfil no topbar', err);
            }
        });
    }

    toggleLayoutConfig() {
        this.layoutConfigVisible = true;
        this.layoutService.changeLayoutConfig();
    }

    logout() {
        this.authService.logout();
    }

    ngOnInit(): void {
        this.isRootRoute.set(this.router.url.split('?')[0] === '/' || this.router.url.split('?')[0] === '' || this.router.url.split('?')[0] === '/dashboard');

        // Tenta recuperar do sessionStorage primeiro para o estado inicial da Topbar,
        // evitando o flash 'SICOM' se houver um valor persistido.
        const savedSistema = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('sicom_sistema') : null;
        this.sistema.set(savedSistema || this.menuService.sistemaAtual);

        // Inscreva-se no serviço para receber as atualizações dinâmicas
        this.menuService.sistema$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((sistema) => {
            this.sistema.set(sistema);
        });
    }

    async profile(): Promise<void> {
        this.opPopover()?.hide();
        this.router.navigate(['portal/profile']);
    }

    removerNotificacao(id: string): void {
        this.notificacoesService.remover(id);
    }

    fecharNotificacoes(): void {
        this.notificacoesPopover()?.hide();
    }

    limparNotificacoes(): void {
        this.notificacoesService.limpar();
    }
}
